import { z } from "zod";
import { email, money, pageQuery, phone, trimmed, uuid } from "./common";

/** CRM, долги, касса (ТЗ 09 §9.1–9.2, ТЗ 07 §7.6–7.7, BR-071…089, BR-141…147). */

export const CUSTOMER_SOURCES = ["pos", "website", "qr", "telegram", "import", "referral", "aggregator"] as const;
export const CUSTOMER_SEGMENTS = ["debtors", "overdue", "inactive", "birthday", "vip", "blocked"] as const;
/** Пустое поле формы = «не указано» */
const blankToNull = (v: unknown) => (typeof v === "string" && v.trim() === "" ? null : v);
const optText = (max: number) => z.string().trim().max(max, { error: `Максимум ${max} символов` }).nullable().optional();

export const customerSchema = z.strictObject({
  firstName: trimmed(1, 80),
  lastName: optText(80),
  phone: z.preprocess(blankToNull, phone.nullable().optional()),
  email: z.preprocess(blankToNull, email.nullable().optional()),
  birthDate: z.iso.date({ error: "Некорректная дата" }).nullable().optional(),
  gender: z.enum(["male", "female"]).nullable().optional(),
  source: z.enum(CUSTOMER_SOURCES).optional(),
  homeBranchId: uuid.nullable().optional(),
  tags: z.array(z.string().trim().min(1).max(30)).max(20).optional(),
  notes: optText(1000),
  marketingConsent: z.boolean().optional(),
  allergies: z.array(z.string().trim().min(1).max(40)).max(20).optional(),
  discountPercent: z.number().min(0, { error: "От 0 до 99.99" }).max(99.99, { error: "От 0 до 99.99" }).nullable().optional(),
  /** Кредит и лимит — только с правом debts.manage (FR-DEBT-001) */
  creditAllowed: z.boolean().optional(),
  creditLimit: money.optional(),
  blocked: z.boolean().optional(),
});
export type CustomerInput = z.input<typeof customerSchema>;
export const customerUpdateSchema = customerSchema.partial().extend({ version: z.number().int().min(1) });
/** Быстрое создание на POS (FR-CRM-004): телефон + имя */
export const customerQuickSchema = z.strictObject({ firstName: trimmed(1, 80), phone });

export const customersQuery = pageQuery.extend({
  segment: z.enum(CUSTOMER_SEGMENTS).optional(),
  tag: z.string().trim().max(30).optional(),
});

export const customerMergeSchema = z.strictObject({ targetId: uuid, sourceIds: z.array(uuid).min(1).max(10) });

// ───────── долги ─────────

export const DEBT_PAY_METHODS = ["cash", "card", "qr"] as const;
/** Погашение (BR-144/145): FIFO по старым долгам либо конкретное начисление; наличные — через кассовую смену. */
export const debtRepaySchema = z.strictObject({
  amount: money.refine((v) => v > 0, { error: "Больше 0" }),
  method: z.enum(DEBT_PAY_METHODS),
  chargeId: uuid.optional(),
  comment: optText(300),
});
/** Ручная корректировка (BR-143): + начисление / − уменьшение, с причиной; право debts.adjust */
export const debtAdjustSchema = z.strictObject({
  amount: z.number().int().min(-1e13).max(1e13).refine((v) => v !== 0, { error: "Сумма не может быть 0" }),
  comment: trimmed(1, 300),
  dueDate: z.iso.date({ error: "Некорректная дата" }).nullable().optional(),
});
/** Списание безнадёжного долга (BR-147): право debts.write_off, причина */
export const debtWriteOffSchema = z.strictObject({ comment: trimmed(1, 300) });
export const debtsReportQuery = z.object({ from: z.iso.date().optional(), to: z.iso.date().optional() });

// ───────── касса ─────────

export const CASH_OPERATION_TYPES = ["cash_in", "cash_out", "collection", "expense", "tip_payout", "correction_in", "correction_out"] as const;
export type CashOperationType = (typeof CASH_OPERATION_TYPES)[number] | "debt_repayment" | "salary_payout" | "supplier_payment" | "supplier_refund" | "banquet_deposit" | "banquet_refund";
/** Внесение/изъятие/инкассация/расход/выплата чаевых требуют причину (BR-086). */
export const cashOperationSchema = z.strictObject({
  type: z.enum(CASH_OPERATION_TYPES),
  amount: money.refine((v) => v > 0, { error: "Больше 0" }),
  reason: trimmed(1, 300),
  /** Ссылка на расход модуля финансов (сам расход не создаётся — без двойного учёта) */
  expenseId: uuid.nullable().optional(),
  employeeId: uuid.optional(),
});
/** Открытие смены веб-кассы (размен) — FR-CASH-002 */
export const shiftOpenSchema = z.strictObject({ cashStartMinor: money.default(0) });
export const shiftsQuery = pageQuery.extend({ status: z.enum(["open", "closed"]).optional() });
